const CMS_API_ORIGINS = {
  prod: 'https://cms-api.sonio-group.com',
  uat: 'https://uat.cms-api.sonio-group.com',
  staging: 'https://staging.cms-api.sonio-group.com',
  dev: 'https://dev.cms-api.sonio-group.com',
} as const;

function consoleHostname(): string {
  if (typeof window === 'undefined') return '';
  return window.location.hostname;
}

/**
 * Test-mode CMS API origin from the console you are logged into:
 * localhost → staging.cms
 * staging.console → staging.cms
 * uat.console → uat.cms
 * console.sonio-group.com → uat.cms
 */
export function getCmsApiOrigin(liveData?: boolean): string {
  if (liveData) {
    return (
      process.env.REACT_APP_LIVE_API_ORIGIN ||
      stripApiSuffix(process.env.REACT_APP_LIVE_API_BASE_URL) ||
      CMS_API_ORIGINS.prod
    );
  }

  const envOrigin =
    process.env.REACT_APP_API_ORIGIN ||
    stripApiSuffix(process.env.REACT_APP_API_BASE_URL);
  if (envOrigin) return envOrigin;

  const host = consoleHostname();
  if (host === 'localhost' || host === '127.0.0.1') return CMS_API_ORIGINS.staging;
  if (host.startsWith('staging.')) return CMS_API_ORIGINS.staging;
  if (host.startsWith('uat.')) return CMS_API_ORIGINS.uat;
  if (host === 'console.sonio-group.com' || host === 'www.console.sonio-group.com') {
    return CMS_API_ORIGINS.uat;
  }
  return CMS_API_ORIGINS.dev;
}

function stripApiSuffix(url?: string): string | undefined {
  if (!url) return undefined;
  return url.replace(/\/api\/v1\/?$/, '').replace(/\/$/, '');
}

export const getCmsApiBaseUrl = (liveData?: boolean) =>
  `${getCmsApiOrigin(liveData)}/api/v1`;

/**
 * Dev Hub Status health endpoint:
 * staging / uat / localhost → staging.api
 * live (prod) → api.sonio-group.com
 */
export function getStatusHealthUrl(liveData?: boolean): string {
  if (liveData) return 'https://api.sonio-group.com/health';
  return 'https://staging.api.sonio-group.com/health';
}

export function isSonioCmsApiUrl(url: string): boolean {
  return url.includes('cms-api.sonio-group.com') || url.startsWith('/api/');
}

const API_BASE_URL = {
  toString() {
    return getCmsApiBaseUrl();
  },
  valueOf() {
    return getCmsApiBaseUrl();
  },
  [Symbol.toPrimitive]() {
    return getCmsApiBaseUrl();
  },
} as unknown as string;

export const API_ENDPOINTS = {
  get USERS() {
    return `${getCmsApiBaseUrl()}/users`;
  },
  USER_LOGIN_HISTORY: (userId: string) =>
    `${getCmsApiBaseUrl()}/users/${encodeURIComponent(userId)}/login-history`,
  get DELETE_USER() {
    return `${getCmsApiBaseUrl()}/user`;
  },
  get CREATE_USER() {
    return `${getCmsApiBaseUrl(true)}/user`;
  },
  get UPDATE_USER() {
    return `${getCmsApiBaseUrl()}/user`;
  },
  get PROFILE() {
    return `${getCmsApiBaseUrl()}/profile`;
  },
  get ASSIGN_ORGANIZATIONS() {
    return `${getCmsApiBaseUrl()}/user/assign_organizations`;
  },
  get REMOVE_ORGANIZATIONS() {
    return `${getCmsApiBaseUrl()}/user/remove_organization`;
  },
  get USER_ORGANIZATIONS() {
    return `${getCmsApiBaseUrl()}/user/organizations`;
  },
  get ORGANIZATIONS() {
    return `${getCmsApiBaseUrl()}/organizations`;
  },
  get ORGANIZATION() {
    return `${getCmsApiBaseUrl()}/organization`;
  },
  get ROLES() {
    return `${getCmsApiBaseUrl()}/roles`;
  },
  get ROLE() {
    return `${getCmsApiBaseUrl()}/role`;
  },
  get PERMISSIONS() {
    return `${getCmsApiBaseUrl()}/permissions`;
  },
  get PERMISSION() {
    return `${getCmsApiBaseUrl()}/permission`;
  },
  get CLIENTS() {
    return `${getCmsApiBaseUrl()}/clients`;
  },
  get MANUAL_REVIEW() {
    return `${getCmsApiBaseUrl()}/manual-review`;
  },
  get SESSIONS_BY_REFERENCE() {
    return `${getCmsApiBaseUrl()}/sessions/by-reference`;
  },
};

export default API_BASE_URL;
